/**
 * Cleans product labels by removing an optional leading numeric prefix while preserving
 * meaningful storage phrases.
 *
 * Behavior:
 * - If the label already starts with a storage phrase like "N Go" or "N To", return it unchanged.
 *   Examples:
 *     - "12 Go" -> "12 Go"
 *     - "1 To"  -> "1 To"
 *
 * - If the label starts with a numeric prefix followed by content:
 *     - If that content begins with a storage phrase ("N Go" / "N To"), drop the prefix and keep the phrase.
 *       Examples:
 *         - "1200 12 Go" -> "12 Go"
 *         - "10000 1 To"    -> "1 To"
 *     - Otherwise, drop the numeric prefix and return the content (product name/model/brand).
 *       Examples:
 *         - "001 iPhone 14"   -> "iPhone 14"
 *         - "012 Google Pixel"-> "Google Pixel"
 *         - "012 Topophone"   -> "Topophone"
 *
 * Notes:
 * - The detection is case-insensitive for units ("Go", "To").
 * - The function normalizes whitespace (trims and collapses multiple spaces).
 * - Extend the unit group `(Go|To)` if you need to support more units (e.g., Mo, GB, TB).
 *
 * @param name - The input label to clean.
 * @returns The cleaned label with the appropriate prefix handling applied.
 */
export function cleanAlgoliaLabel(name: string) {
  // If input is not a string, return it as-is (maintains original behavior).
  if (typeof name !== 'string') {
    return name as unknown as string
  }

  // Normalize whitespace:
  // - trim leading/trailing spaces
  // - collapse multiple spaces to a single space
  const normalized = name.trim().replace(/\s+/g, ' ')

  // 1) If the label already starts with a storage phrase "N Go" or "N To",
  //    we should keep it unchanged (e.g., "12 Go", "1 To").
  //    Regex explanation:
  //    - ^\d+\s+  => begins with one or more digits and at least one space
  //    - (Go|To)\b => followed by unit "Go" or "To" as a whole word
  //    - /i        => case-insensitive
  const startsWithStoragePhrase = /^\d+\s+(Go|To)\b/i.test(normalized)
  if (startsWithStoragePhrase) {
    return normalized
  }

  // 2) Check for a leading numeric prefix "<number> <content>".
  //    If there's no match, there is no removable prefix → return as-is.
  //    Example of a match: "001 iPhone 14" -> groups: "001", "iPhone 14"
  const prefixMatch = normalized.match(/^(\d+)\s+(.*)$/)
  if (!prefixMatch) {
    return normalized
  }

  // Keep the parts explicit for readability (no destructuring).
  const contentAfterPrefix = prefixMatch[2]

  // 3) If the content after the prefix begins with a storage phrase "N Go" or "N To",
  //    we drop the prefix and keep that phrase.
  //    Examples:
  //      - "1200 12 Go"    -> "12 Go"
  //      - "10000 1 To"    -> "1 To"
  const contentStartsWithStoragePhrase = /^\d+\s+(Go|To)\b/i.test(
    contentAfterPrefix,
  )
  if (contentStartsWithStoragePhrase) {
    return contentAfterPrefix
  }

  // 4) Otherwise, the leading number is just a prefix before a product name/model/brand.
  //    Drop it and return the content.
  //    Examples:
  //      - "001 iPhone 14"    -> "iPhone 14"
  //      - "012 Google Pixel" -> "Google Pixel"
  //      - "012 Topophone"    -> "Topophone"
  return contentAfterPrefix
}
